import SidebarGroup from "../sidebarGroup"
import useLayout from "hooks/useLayout"
import SidebarRow from "../SidebarRow"
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as HistoryIcon } from 'assets/icons/restore.svg'
import RightSidebar from "./RightSidebar"
import { useTranslation } from "react-i18next"
import useAccess from "@/hooks/useAccess"
import useSuppliers from "@/hooks/useSuppliers"
import { AccessRole } from "@/types"
import Dialog from "@/components/shared/dialog"
import DeleteSupplierForm from "@/features/storages/suppliers/deleteSupplierForm"
import { useState } from "react"

interface RightSidebarSupplierProps {
    className?: string;
    children?: React.ReactNode;
    storageId?: string;
    supplierId?: string;
}

const RightSidebarSupplier: React.FC<RightSidebarSupplierProps> = ({ className, children, storageId, supplierId }) => {
    
    const { t } = useTranslation()
    
    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const {
        supplier,
        restoreSupplierAsync,
        isRestoring
    } = useSuppliers({ storageId, supplierId })

    const {
        openDialog,
        closeDialog,
        addToast
    } = useLayout()

    const [isRestoringState, setIsRestoringState] = useState(false)

    const handleDeleteClick = () => {
        openDialog(`delete_supplier`)
    }

    const handleRestoreClick = async () => {
        if (!storageId || !supplierId) return
        setIsRestoringState(true)
        try {
            await restoreSupplierAsync({
                storageId,
                supplierId
            })
            addToast({
                text: t('supplierRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    // Return the sidebar component.
    return (
        <>
            <RightSidebar storageId={storageId}>
                <SidebarGroup title={t('menuSupplierActions')} allwaysOpen={true}>
                    <SidebarRow 
                        text={t('buttonViewHistory')} 
                        style={`border`} 
                        onClick={() => openDialog('storage_history')}
                        icon={<HistoryIcon />}
                    />
                    {limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) &&
                        <>
                            {!supplier?.isDeleted && (
                                <>
                                    <SidebarRow 
                                        to={`/storage/suppliers/${supplierId}/edit`} 
                                        text={t('buttonEdit')} 
                                        icon={<EditIcon />} 
                                        style={`border`} 
                                    />
                                    <SidebarRow 
                                        onClick={handleDeleteClick} 
                                        text={t('buttonDelete')} 
                                        icon={<DeleteIcon />} 
                                        style={`border`} 
                                    />
                                </>
                            )}
                            {supplier?.isDeleted && (
                                <SidebarRow 
                                    onClick={handleRestoreClick} 
                                    text={isRestoring || isRestoringState ? t('restoringItem') : t('restoreSupplier')} 
                                    style={`border`} 
                                />
                            )}
                        </>
                    }
                </SidebarGroup>
            </RightSidebar>
            
            {supplier && storageId && supplierId && (
                <Dialog
                    dialogId="delete_supplier"
                    title={t('titleDeleteSupplier')}
                    colorScheme="red"
                    mobileFullScreen={true}
                    displayCloseButton={false}
                >
                    <DeleteSupplierForm
                        supplier={supplier}
                        storageId={storageId}
                        supplierId={supplierId}
                        onCancel={closeDialog}
                        onSuccess={closeDialog}
                    />
                </Dialog>
            )}
        </>
    )
}

export default RightSidebarSupplier
